import React, { useMemo, useState } from 'react'
import PropTypes from 'prop-types'
import moment from 'moment'
import { Row, Col } from 'react-bootstrap'

import Button from 'components/Button'

const PILLAR_ORDER = [
  'Inspirational Visionary',
  'Relationship Builder',
  'Manager of Execution'
]

const PILLAR_COLORS = {
  'Inspirational Visionary': '#7B61FF',
  'Relationship Builder': '#3F8CFF',
  'Manager of Execution': '#34C77B'
}

// The competency's 0-100 score is computed at generation time as a weighted
// average of per-meeting scores (see backend services/profile-generation/
// generate.js: computeAggregateScore). The frontend just reads aggregateScore
// when present; the inline formula below is a fallback for old rows generated
// before the column existed.
const computeDisplayScore = c => {
  if (!c) return null
  if (c.aggregateScore != null) {
    const agg = Number(c.aggregateScore)
    if (Number.isFinite(agg)) return Math.round(agg)
  }
  // Number(null) === 0, which is finite — guard against null explicitly
  // so a competency with no high-signal meetings doesn't get a fallback score.
  if (c.proficiencyScore == null) return null
  const proficiency = Number(c.proficiencyScore)
  if (!Number.isFinite(proficiency)) return null
  const total = Number(c.totalOpportunities)
  if (!Number.isFinite(total) || total <= 0) return null
  const utilized = Number(c.utilizedOpportunities) || 0
  const coverage = Math.max(0, Math.min(1, utilized / total))
  const normProficiency = Math.max(
    0,
    Math.min(1, (proficiency - 1) / 3)
  )
  return Math.round(
    (coverage * 0.6 + normProficiency * 0.4) * 100
  )
}

// score is a 0–100 integer (or null)
const scoreColor = score => {
  if (score == null) return '#B5BEC6'
  if (score >= 75) return '#34C77B'
  if (score >= 50) return '#E0B341'
  if (score >= 25) return '#E08841'
  return '#E04141'
}

const formatScore = score => (score == null ? 'N/A' : score)

const formatRichness = level => {
  if (level == null) return null
  if (level >= 3) return 'High richness'
  if (level >= 2) return 'Medium richness'
  if (level >= 1) return 'Low richness'
  return 'Minimal richness'
}

const HIGH_RICHNESS_THRESHOLD = 2

// 0-100 display score for a single (competency, meeting) row.
const perMeetingDisplayScore = m => {
  if (!m) return null
  const proficiency = Number(m.proficiencyScore)
  if (!Number.isFinite(proficiency)) return null
  const total = Number(m.totalOpportunities)
  if (!Number.isFinite(total) || total <= 0) return null
  const utilized = Number(m.utilizedOpportunities) || 0
  const coverage = Math.max(0, Math.min(1, utilized / total))
  const normProficiency = Math.max(
    0,
    Math.min(1, (proficiency - 1) / 3)
  )
  return Math.round(
    (coverage * 0.6 + normProficiency * 0.4) * 100
  )
}

// Weight, mirroring the leadership-platform reference impl:
//   w = (N / (N+8)) * ((R+1) / 4)
// where N = totalOpportunities for this (meeting, competency)
//       R = opportunityRichness 0-3
const perMeetingWeight = m => {
  if (!m) return 0
  const total = Number(m.totalOpportunities) || 0
  const richness = Number(m.richness) || 0
  return (total / (total + 8)) * ((richness + 1) / 4)
}

const SCORE_BUCKETS = [
  { label: '0-25', min: 0, max: 25 },
  { label: '25-50', min: 25, max: 50 },
  { label: '50-75', min: 50, max: 75 },
  { label: '75-100', min: 75, max: 101 }
]

const PillarChart = ({ pillarScores }) => {
  const maxBarHeight = 200
  const labelReserve = 28
  const plotHeight = maxBarHeight + labelReserve
  const gridLine = {
    position: 'absolute',
    left: 0,
    right: 0,
    height: 1,
    backgroundColor: '#E5E9EE'
  }
  const yLabel = {
    position: 'absolute',
    left: 0,
    fontSize: 12,
    color: '#7B8794',
    transform: 'translateY(-50%)'
  }
  return (
    <div className="mb-10 p-20 border rounded bg-white">
      <div className="d-flex">
        {/* Y-axis label column */}
        <div
          className="mr-15"
          style={{
            position: 'relative',
            width: 30,
            height: plotHeight,
            flexShrink: 0
          }}
        >
          <span style={{ ...yLabel, top: labelReserve }}>
            100
          </span>
          <span
            style={{
              ...yLabel,
              top: labelReserve + maxBarHeight / 2
            }}
          >
            50
          </span>
          <span
            style={{
              ...yLabel,
              top: labelReserve + maxBarHeight
            }}
          >
            0
          </span>
        </div>

        {/* Plot area */}
        <div
          className="flex-grow-1"
          style={{ position: 'relative', height: plotHeight }}
        >
          {/* horizontal grid lines */}
          <div style={{ ...gridLine, top: labelReserve }} />
          <div
            style={{
              ...gridLine,
              top: labelReserve + maxBarHeight / 2
            }}
          />
          <div
            style={{
              ...gridLine,
              top: labelReserve + maxBarHeight
            }}
          />

          {/* bars */}
          <div
            className="d-flex justify-content-around align-items-end"
            style={{ height: plotHeight, position: 'relative' }}
          >
            {PILLAR_ORDER.map(pillar => {
              const score = pillarScores[pillar]
              const height =
                score != null
                  ? Math.max(8, (score / 100) * maxBarHeight)
                  : 8
              const color = PILLAR_COLORS[pillar] || '#7B61FF'
              return (
                <div
                  key={pillar}
                  className="text-center"
                  style={{ flex: 1, maxWidth: 200 }}
                >
                  <div
                    className="mx-auto"
                    style={{
                      width: 60,
                      height: plotHeight,
                      display: 'flex',
                      flexDirection: 'column',
                      justifyContent: 'flex-end',
                      alignItems: 'center'
                    }}
                  >
                    <p
                      className="font-weight-bold mb-2"
                      style={{ fontSize: 16 }}
                    >
                      {formatScore(score)}
                    </p>
                    <div
                      style={{
                        width: '100%',
                        height,
                        backgroundColor: color,
                        borderRadius: 4
                      }}
                    />
                  </div>
                </div>
              )
            })}
          </div>
        </div>
      </div>

      {/* pillar labels under bars (aligned with bar centers) */}
      <div className="d-flex" style={{ paddingLeft: 45 }}>
        {PILLAR_ORDER.map(pillar => (
          <div
            key={pillar}
            className="text-center text-gray-dark mt-10"
            style={{ flex: 1, fontSize: 13 }}
          >
            {pillar}
          </div>
        ))}
      </div>
    </div>
  )
}

PillarChart.propTypes = {
  pillarScores: PropTypes.object.isRequired
}

// Generic themed insight card (Strengths or Growth Edges). Shows the first
// insight by default and exposes a "Show N more" toggle.
const InsightCard = ({
  title,
  icon,
  items,
  meetingCount,
  emptyText,
  colorScheme
}) => {
  const [expanded, setExpanded] = useState(false)
  const list = Array.isArray(items) ? items : []
  const visible = expanded ? list : list.slice(0, 1)
  const remaining = Math.max(0, list.length - 1)

  return (
    <div
      style={{
        backgroundColor: colorScheme.bg,
        border: `1px solid ${colorScheme.border}`,
        borderRadius: 8,
        padding: 20,
        height: '100%'
      }}
    >
      <div className="d-flex align-items-center mb-15">
        <span
          style={{
            color: colorScheme.accent,
            fontSize: 18,
            marginRight: 8
          }}
        >
          {icon}
        </span>
        <span
          className="font-weight-bold"
          style={{ color: colorScheme.accent, fontSize: 15 }}
        >
          {title}
        </span>
        <span
          className="ml-10 text-gray-dark"
          style={{ fontSize: 12 }}
        >
          {meetingCount != null
            ? `(synthesized from ${meetingCount} ${
                meetingCount === 1 ? 'meeting' : 'meetings'
              })`
            : ''}
        </span>
      </div>

      {list.length === 0 ? (
        <p
          className="mb-0"
          style={{ color: colorScheme.accent, fontSize: 13 }}
        >
          {emptyText}
        </p>
      ) : (
        <>
          {visible.map((item, idx) => (
            <div
              key={idx}
              className={
                idx === visible.length - 1 ? '' : 'mb-15'
              }
              style={{
                borderLeft: `3px solid ${colorScheme.accent}`,
                paddingLeft: 12
              }}
            >
              <p
                className="mb-5"
                style={{
                  fontStyle: 'italic',
                  color: colorScheme.accent
                }}
              >
                “{item.quote}”
              </p>
              <p
                className="mb-0"
                style={{
                  color: colorScheme.body,
                  fontSize: 13
                }}
              >
                {item.explanation}
              </p>
            </div>
          ))}
          {remaining > 0 && (
            <button
              type="button"
              onClick={() => setExpanded(prev => !prev)}
              className="mt-15 p-0"
              style={{
                background: 'transparent',
                border: 'none',
                color: colorScheme.accent,
                fontSize: 13,
                fontWeight: 600,
                cursor: 'pointer'
              }}
            >
              {expanded ? 'Show less' : `Show ${remaining} more`}
            </button>
          )}
        </>
      )}
    </div>
  )
}

InsightCard.propTypes = {
  title: PropTypes.string.isRequired,
  icon: PropTypes.node,
  items: PropTypes.array,
  meetingCount: PropTypes.number,
  emptyText: PropTypes.string.isRequired,
  colorScheme: PropTypes.object.isRequired
}

const STRENGTH_THEME = {
  bg: '#F0FBF5',
  border: '#BFEBD2',
  accent: '#1F9D55',
  body: '#1F6B3D'
}

const GROWTH_THEME = {
  bg: '#FEF8E7',
  border: '#F2D98C',
  accent: '#B8740C',
  body: '#8A5B0E'
}

const ScoreDistribution = ({ meetingScores }) => {
  const usable = meetingScores.filter(
    m => m.richness >= HIGH_RICHNESS_THRESHOLD
  )
  const scores = usable
    .map(perMeetingDisplayScore)
    .filter(s => s != null)
  const counts = SCORE_BUCKETS.map(
    b => scores.filter(s => s >= b.min && s < b.max).length
  )
  const maxCount = Math.max(...counts, 1)
  const maxBarHeight = 110

  return (
    <div
      className="p-20 border rounded"
      style={{ height: '100%' }}
    >
      <p
        className="font-weight-bold mb-15"
        style={{ fontSize: 14 }}
      >
        Score Distribution
      </p>
      <div
        className="d-flex align-items-end justify-content-around"
        style={{ height: maxBarHeight + 24 }}
      >
        {SCORE_BUCKETS.map((b, i) => {
          const count = counts[i]
          const height = count
            ? Math.max(8, (count / maxCount) * maxBarHeight)
            : 0
          return (
            <div
              key={b.label}
              className="text-center"
              style={{ flex: 1 }}
            >
              <div
                style={{
                  height: maxBarHeight,
                  display: 'flex',
                  alignItems: 'flex-end',
                  justifyContent: 'center'
                }}
              >
                {count > 0 && (
                  <div
                    style={{
                      width: 40,
                      height,
                      backgroundColor: '#34C77B',
                      borderRadius: 4,
                      position: 'relative'
                    }}
                  >
                    <span
                      className="font-weight-bold"
                      style={{
                        position: 'absolute',
                        top: -22,
                        left: '50%',
                        transform: 'translateX(-50%)',
                        fontSize: 13
                      }}
                    >
                      {count}
                    </span>
                  </div>
                )}
              </div>
            </div>
          )
        })}
      </div>
      <div className="d-flex justify-content-around mt-5">
        {SCORE_BUCKETS.map(b => (
          <span
            key={b.label}
            className="text-gray-dark text-center"
            style={{ fontSize: 12, flex: 1 }}
          >
            {b.label}
          </span>
        ))}
      </div>
      <p
        className="text-center text-gray-dark mt-10 mb-0"
        style={{ fontSize: 12 }}
      >
        {scores.length}{' '}
        {scores.length === 1 ? 'conversation' : 'conversations'}{' '}
        analyzed
      </p>
    </div>
  )
}

ScoreDistribution.propTypes = {
  meetingScores: PropTypes.array.isRequired
}

const SIGNAL_PILL = {
  0: { label: 'No signal', bg: '#F1F5F8', fg: '#6B7B85' },
  1: { label: 'Low signal', bg: '#F1F5F8', fg: '#6B7B85' },
  2: { label: 'Medium signal', bg: '#FEF8E7', fg: '#B8740C' },
  3: { label: 'Strong signal', bg: '#F0FBF5', fg: '#1F9D55' }
}

const signalPill = richness => {
  const r = Math.max(
    0,
    Math.min(3, Math.round(Number(richness) || 0))
  )
  return SIGNAL_PILL[r]
}

const SignalChip = ({ richness }) => {
  const pill = signalPill(richness)
  return (
    <span
      style={{
        backgroundColor: pill.bg,
        color: pill.fg,
        fontSize: 11,
        fontWeight: 600,
        padding: '2px 8px',
        borderRadius: 10,
        whiteSpace: 'nowrap'
      }}
    >
      {pill.label}
    </span>
  )
}

SignalChip.propTypes = {
  richness: PropTypes.number
}

const IndividualScoresList = ({ meetingScores, sources }) => {
  const sourcesByExternalId = {}
  ;(sources || []).forEach(s => {
    sourcesByExternalId[s.externalId] = s
  })

  const titleFor = m =>
    (sourcesByExternalId[m.externalId] &&
      sourcesByExternalId[m.externalId].title) ||
    'Untitled meeting'

  const contributing = meetingScores.filter(
    m => m.richness >= HIGH_RICHNESS_THRESHOLD
  )
  const notCounted = meetingScores.filter(
    m => m.richness < HIGH_RICHNESS_THRESHOLD
  )

  // Normalize per-meeting weight into a contribution share that sums to
  // 100% across the included rows. Matches what's actually being averaged.
  const totalWeight = contributing.reduce(
    (s, m) => s + perMeetingWeight(m),
    0
  )

  return (
    <div
      className="p-20 border rounded"
      style={{ height: '100%' }}
    >
      <p
        className="font-weight-bold mb-15"
        style={{ fontSize: 14 }}
      >
        Per-meeting scores
      </p>

      {meetingScores.length === 0 && (
        <p
          className="text-gray-dark mb-0"
          style={{ fontSize: 13 }}
        >
          No meeting analysis available.
        </p>
      )}

      {contributing.length > 0 && (
        <>
          <p
            className="text-gray-dark text-uppercase mb-10"
            style={{ fontSize: 11, letterSpacing: 0.5 }}
          >
            Contributing to this score
          </p>
          {contributing.map(m => {
            const w = perMeetingWeight(m)
            const contribution =
              totalWeight > 0
                ? Math.round((w / totalWeight) * 100)
                : 0
            const score = perMeetingDisplayScore(m)
            return (
              <div
                key={`${m.competencyKey}-${m.externalId}`}
                className="d-flex align-items-center mb-10"
                style={{ fontSize: 13 }}
              >
                <span
                  className="flex-grow-1 mr-10"
                  style={{
                    overflow: 'hidden',
                    textOverflow: 'ellipsis',
                    whiteSpace: 'nowrap'
                  }}
                >
                  {titleFor(m)}
                </span>
                <span className="mr-10">
                  <SignalChip richness={m.richness} />
                </span>
                <span
                  className="text-gray-dark mr-10"
                  style={{
                    fontSize: 12,
                    minWidth: 100,
                    textAlign: 'right'
                  }}
                >
                  Contribution: {contribution}%
                </span>
                <span
                  className="font-weight-bold"
                  style={{
                    fontSize: 13,
                    minWidth: 36,
                    textAlign: 'right'
                  }}
                >
                  {score != null ? score : '—'}
                </span>
              </div>
            )
          })}
        </>
      )}

      {notCounted.length > 0 && (
        <>
          <p
            className="text-gray-dark text-uppercase mb-10 mt-15"
            style={{ fontSize: 11, letterSpacing: 0.5 }}
          >
            Not counted
          </p>
          {notCounted.map(m => (
            <div
              key={`${m.competencyKey}-${m.externalId}`}
              className="d-flex align-items-center mb-10"
              style={{ fontSize: 13 }}
            >
              <span
                className="flex-grow-1 mr-10 text-gray-dark"
                style={{
                  overflow: 'hidden',
                  textOverflow: 'ellipsis',
                  whiteSpace: 'nowrap'
                }}
              >
                {titleFor(m)}
              </span>
              <span className="mr-10">
                <SignalChip richness={m.richness} />
              </span>
              <span
                className="text-gray-dark"
                style={{ fontSize: 12, whiteSpace: 'nowrap' }}
              >
                Not enough leadership signal
              </span>
            </div>
          ))}
        </>
      )}
    </div>
  )
}

IndividualScoresList.propTypes = {
  meetingScores: PropTypes.array.isRequired,
  sources: PropTypes.array
}

const CompetencyRow = ({
  competency,
  sources,
  meetingScores
}) => {
  const [expanded, setExpanded] = useState(false)
  const usableMeetingCount = meetingScores.filter(
    m => m.richness >= HIGH_RICHNESS_THRESHOLD
  ).length
  const score = computeDisplayScore(competency)
  const color = scoreColor(score)
  const barWidth = score != null ? `${score}%` : '0%'

  return (
    <div className="border-bottom">
      <button
        type="button"
        onClick={() => setExpanded(prev => !prev)}
        className="d-flex align-items-center w-100 px-15 py-10 bg-white"
        style={{ border: 'none', textAlign: 'left' }}
      >
        <span
          className="mr-15 text-gray-dark"
          style={{
            fontSize: 28,
            lineHeight: 1,
            width: 18,
            flexShrink: 0,
            display: 'inline-block',
            transform: expanded ? 'rotate(90deg)' : 'none',
            transition: 'transform 0.15s ease'
          }}
        >
          ›
        </span>
        <p
          className="font-weight-bold mb-0 mr-15"
          style={{ fontSize: 15, minWidth: 200 }}
        >
          {competency.competencyName}
        </p>
        <div
          className="flex-grow-1 mr-15"
          style={{
            height: 6,
            backgroundColor: '#E5E9EE',
            borderRadius: 3,
            overflow: 'hidden'
          }}
        >
          <div
            style={{
              width: barWidth,
              height: '100%',
              backgroundColor: color
            }}
          />
        </div>
        <p
          className="font-weight-bold mb-0"
          style={{
            fontSize: 15,
            color,
            minWidth: 32,
            textAlign: 'right'
          }}
        >
          {formatScore(score)}
        </p>
      </button>

      {expanded && (
        <div className="p-20 bg-gray-light">
          <Row>
            <Col xs={12} md={6} className="mb-20">
              <ScoreDistribution meetingScores={meetingScores} />
            </Col>
            <Col xs={12} md={6} className="mb-20">
              <IndividualScoresList
                meetingScores={meetingScores}
                sources={sources}
              />
            </Col>
            <Col xs={12} md={6} className="mb-20">
              <InsightCard
                title="Strengths"
                icon="✓"
                items={competency.strengths}
                meetingCount={usableMeetingCount}
                emptyText="No strength evidence captured for this competency."
                colorScheme={STRENGTH_THEME}
              />
            </Col>
            <Col xs={12} md={6} className="mb-20">
              <InsightCard
                title="Growth Edges"
                icon="↗"
                items={competency.growthEdges}
                meetingCount={usableMeetingCount}
                emptyText="No growth-edge evidence captured for this competency."
                colorScheme={GROWTH_THEME}
              />
            </Col>
          </Row>
        </div>
      )}
    </div>
  )
}

CompetencyRow.propTypes = {
  competency: PropTypes.object.isRequired,
  sources: PropTypes.array,
  meetingScores: PropTypes.array.isRequired
}

const ProfileDetail = ({ profile, onBack }) => {
  const competencies = useMemo(
    () => profile.competencies || [],
    [profile]
  )
  const sources = profile.sources || []
  const meetingScores = profile.meetingScores || []

  // Group per-meeting scores by competencyKey, ordered to match the
  // source meetings list so the Individual Scores panel renders in a
  // stable order across competencies.
  const meetingScoresByCompetency = useMemo(() => {
    const sourceOrder = sources.map(s => s.externalId)
    const orderIndex = id => {
      const i = sourceOrder.indexOf(id)
      return i === -1 ? Number.MAX_SAFE_INTEGER : i
    }
    const buckets = {}
    meetingScores.forEach(m => {
      if (!buckets[m.competencyKey])
        buckets[m.competencyKey] = []
      buckets[m.competencyKey].push(m)
    })
    Object.keys(buckets).forEach(key => {
      buckets[key].sort(
        (a, b) =>
          orderIndex(a.externalId) - orderIndex(b.externalId)
      )
    })
    return buckets
  }, [meetingScores, sources])

  const pillarScores = useMemo(() => {
    const buckets = {}
    competencies.forEach(c => {
      if (!c.pillar) return
      const score = computeDisplayScore(c)
      if (score == null) return
      if (!buckets[c.pillar]) buckets[c.pillar] = []
      buckets[c.pillar].push(score)
    })
    const result = {}
    Object.keys(buckets).forEach(pillar => {
      const arr = buckets[pillar]
      result[pillar] = Math.round(
        arr.reduce((a, b) => a + b, 0) / arr.length
      )
    })
    return result
  }, [competencies])

  const sortedCompetencies = useMemo(() => {
    return [...competencies].sort((a, b) => {
      const sa = computeDisplayScore(a)
      const sb = computeDisplayScore(b)
      const va = sa == null ? -1 : sa
      const vb = sb == null ? -1 : sb
      return vb - va
    })
  }, [competencies])

  const completedLabel = profile.completedAt
    ? moment(profile.completedAt).format('MMM D, YYYY')
    : moment(profile.createdAt).format('MMM D, YYYY')

  return (
    <div>
      {onBack && (
        <div className="mb-10">
          <Button
            variant="link"
            smallPadding
            onClick={onBack}
            style={{ paddingLeft: 0 }}
          >
            ← Back
          </Button>
        </div>
      )}
      <div className="mb-20">
        <p
          className="font-weight-bold mb-0"
          style={{ fontSize: 20 }}
        >
          Leadership profile · {completedLabel}
        </p>
        <p className="text-gray-dark mb-0">
          {profile.meetingCount}{' '}
          {profile.meetingCount === 1 ? 'meeting' : 'meetings'}
          {profile.modelUsed ? ` · ${profile.modelUsed}` : ''}
        </p>
      </div>

      {profile.summary && (
        <p
          className="mb-20 p-15 bg-gray-light rounded"
          style={{ fontSize: 14 }}
        >
          {profile.summary}
        </p>
      )}

      <h3 className="mb-15" style={{ fontSize: 18 }}>
        Three Pillars of Leadership
      </h3>
      <PillarChart pillarScores={pillarScores} />
      <p
        className="text-right text-gray-dark mt-1 mb-30"
        style={{ fontSize: 10 }}
      >
        Framework: Hagberg Consulting Group
      </p>

      <h3 className="mb-15" style={{ fontSize: 18 }}>
        Overall Competency Scores
      </h3>
      <div className="border rounded mb-30">
        {sortedCompetencies.map(c => (
          <CompetencyRow
            key={c.competencyKey}
            competency={c}
            sources={sources}
            meetingScores={
              meetingScoresByCompetency[c.competencyKey] || []
            }
          />
        ))}
      </div>

      {sources.length > 0 && (
        <>
          <h3 className="mb-15" style={{ fontSize: 18 }}>
            Source meetings
          </h3>
          <div className="border rounded p-15 mb-30">
            {sources.map(s => (
              <div
                key={s.externalId || s.id}
                className="d-flex justify-content-between mb-10"
              >
                <div>
                  <p className="mb-0" style={{ fontSize: 14 }}>
                    {s.title || 'Untitled meeting'}
                  </p>
                  <p
                    className="text-gray-dark mb-0"
                    style={{ fontSize: 12 }}
                  >
                    {s.meetingDate
                      ? moment(s.meetingDate).format(
                          'MMM D, YYYY'
                        )
                      : '—'}
                    {s.durationMinutes
                      ? ` · ${s.durationMinutes} min`
                      : ''}
                  </p>
                </div>
                {formatRichness(s.maxRichness) && (
                  <p
                    className="text-gray-dark mb-0"
                    style={{ fontSize: 12 }}
                  >
                    {formatRichness(s.maxRichness)}
                  </p>
                )}
              </div>
            ))}
          </div>
        </>
      )}
    </div>
  )
}

ProfileDetail.propTypes = {
  profile: PropTypes.object.isRequired,
  onBack: PropTypes.func
}

export default ProfileDetail
